import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiDollarSign,
  FiSearch,
  FiRefreshCw,
  FiTruck,
  FiMapPin,
  FiImage,
} from "react-icons/fi";
import { mealsAPI, vendorsAPI } from "../../services/api";
import toast from "react-hot-toast";

const MealManagementContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;
`;

const MealManagementContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 1.125rem;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid #2d5016;
  background: ${(props) => (props.$primary ? "#2d5016" : "white")};
  color: ${(props) => (props.$primary ? "white" : "#2d5016")};
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.$primary ? "#1a3a0e" : "#f8f9fa")};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const FiltersSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchBar = styled.div`
  flex: 1;
  min-width: 200px;
  position: relative;

  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
  }

  input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 0.875rem;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 150px;

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #333;
  }

  select {
    padding: 0.75rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 0.875rem;
    background: white;
    cursor: pointer;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }
  }
`;

const MealsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MealCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }
`;

const MealImage = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 3rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 1rem;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover .overlay {
    opacity: 1;
  }
`;

const StatusBadge = styled.span`
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  &.active {
    background: #d4edda;
    color: #155724;
  }

  &.inactive {
    background: #f8d7da;
    color: #721c24;
  }
`;

const MealContent = styled.div`
  padding: 1.5rem;
`;

const MealHeader = styled.div`
  margin-bottom: 1rem;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d5016;
    margin: 0 0 0.5rem 0;
  }

  .vendor-name {
    color: #666;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 0.875rem;
    margin: 0;
    line-height: 1.4;
  }
`;

const PriceSection = styled.div`
  margin-bottom: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;

  .price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .price-label {
    font-size: 0.875rem;
    color: #666;
  }

  .price {
    font-size: 1.25rem;
    font-weight: 700;
    color: #2d5016;
  }

  .delivery-fee {
    font-size: 0.875rem;
    color: #999;
  }
`;

const FulfillmentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;

  &.delivery {
    background: #e8f5e8;
    color: #2d5016;
  }

  &.pickup {
    background: #fff3e0;
    color: #e65100;
  }
`;

const MealActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
`;

const ActionIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #e0e0e0;
  background: white;
  color: #666;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #2d5016;
    color: #2d5016;
  }

  &.delete:hover {
    border-color: #dc3545;
    color: #dc3545;
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.125rem;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;

  h3 {
    margin-bottom: 1rem;
    color: #333;
  }
`;

const MealManagement = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");

  // Fetch all vendors for filter dropdown
  const { data: vendorsData } = useQuery(
    "vendors",
    () => vendorsAPI.getVendors(),
    { keepPreviousData: true }
  );

  // Fetch all meals
  const {
    data: mealsData,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ["meals", vendorFilter],
    () => mealsAPI.getMeals({ vendorId: vendorFilter || undefined }),
    { keepPreviousData: true }
  );

  const deleteMealMutation = useMutation(
    (mealId) => mealsAPI.deleteMeal(mealId),
    {
      onSuccess: () => {
        toast.success("Meal deleted successfully!");
        queryClient.invalidateQueries("meals");
      },
      onError: (error) => {
        console.error("Error deleting meal:", error);
        toast.error(error.response?.data?.message || "Failed to delete meal");
      },
    }
  );

  const vendors = vendorsData?.data?.data?.vendors || [];
  const meals = mealsData?.data?.data?.meals || [];

  // Filter meals based on search term
  const filteredMeals = meals.filter((meal) => {
    const matchesSearch =
      meal.meal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      meal.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      meal.business_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleCreateMeal = () => {
    navigate("/admin/meals/create");
  };

  const handleEditMeal = (mealId) => {
    navigate(`/admin/meals/${mealId}/edit`);
  };

  const handleDeleteMeal = (mealId) => {
    toast(
      (t) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={{ fontWeight: "bold" }}>Delete Meal</div>
          <div>
            Are you sure you want to delete this meal? This action cannot be
            undone.
          </div>
          <div
            style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
          >
            <button
              onClick={() => {
                toast.dismiss(t.id);
              }}
              style={{
                padding: "5px 15px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                background: "white",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                deleteMealMutation.mutate(mealId);
              }}
              style={{
                padding: "5px 15px",
                border: "none",
                borderRadius: "4px",
                background: "#dc3545",
                color: "white",
                cursor: "pointer",
              }}
            >
              Delete
            </button>
          </div>
        </div>
      ),
      {
        duration: Infinity,
        id: "delete-confirmation",
      }
    );
  };

  if (isLoading) {
    return (
      <MealManagementContainer>
        <MealManagementContent>
          <PageHeader>
            <h1>Meal Management</h1>
            <p>Manage standalone meals for all vendors</p>
          </PageHeader>
          <LoadingState>Loading meals...</LoadingState>
        </MealManagementContent>
      </MealManagementContainer>
    );
  }

  if (error) {
    return (
      <MealManagementContainer>
        <MealManagementContent>
          <PageHeader>
            <h1>Meal Management</h1>
            <p>Manage standalone meals for all vendors</p>
          </PageHeader>
          <EmptyState>
            <h3>Error Loading Meals</h3>
            <p>Failed to load meal data. Please try again.</p>
            <Button onClick={() => refetch()} style={{ marginTop: "1rem" }}>
              <FiRefreshCw />
              Retry
            </Button>
          </EmptyState>
        </MealManagementContent>
      </MealManagementContainer>
    );
  }

  return (
    <MealManagementContainer>
      <MealManagementContent>
        <PageHeader>
          <h1>Meal Management</h1>
          <p>Manage standalone meals for all vendors</p>
        </PageHeader>

        <HeaderActions>
          <Button onClick={() => refetch()}>
            <FiRefreshCw />
            Refresh
          </Button>
          <Button $primary onClick={handleCreateMeal}>
            <FiPlus />
            Add Meal
          </Button>
        </HeaderActions>

        <FiltersSection>
          <FiltersRow>
            <SearchBar>
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search meals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBar>

            <FilterGroup>
              <label>Vendor</label>
              <select
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
              >
                <option value="">All Vendors</option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.businessName}
                  </option>
                ))}
              </select>
            </FilterGroup>
          </FiltersRow>
        </FiltersSection>

        {filteredMeals.length === 0 ? (
          <EmptyState>
            <h3>No meals found</h3>
            <p>No meals match your search criteria.</p>
          </EmptyState>
        ) : (
          <MealsGrid>
            {filteredMeals.map((meal, index) => (
              <MealCard
                key={meal.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <MealImage>
                  {meal.image_url || meal.imageUrl ? (
                    <img
                      src={meal.image_url || meal.imageUrl}
                      alt={meal.meal_name}
                    />
                  ) : (
                    <FiImage />
                  )}
                </MealImage>

                <MealContent>
                  <MealHeader>
                    <h3>{meal.meal_name}</h3>
                    <div className="vendor-name">by {meal.business_name}</div>
                    <p>{meal.description || "No description"}</p>
                  </MealHeader>

                  <PriceSection>
                    <div className="price-row">
                      <span className="price-label">Base Price</span>
                      <span className="price">
                        GYD ${Math.round(parseFloat(meal.base_price || 0)).toLocaleString()}
                      </span>
                    </div>
                    {parseFloat(meal.delivery_fee || 0) > 0 && (
                      <div className="price-row">
                        <span className="price-label">Delivery Fee</span>
                        <span className="delivery-fee">
                          +GYD ${Math.round(parseFloat(meal.delivery_fee || 0)).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </PriceSection>

                  <div style={{ marginBottom: "1rem" }}>
                    {meal.offers_delivery && (
                      <FulfillmentBadge className="delivery">
                        <FiTruck />
                        Delivery
                      </FulfillmentBadge>
                    )}
                    {meal.offers_pickup && (
                      <FulfillmentBadge className="pickup">
                        <FiMapPin />
                        Pickup
                      </FulfillmentBadge>
                    )}
                  </div>

                  <MealActions>
                    <ActionIconButton
                      onClick={() => handleEditMeal(meal.id)}
                      title="Edit"
                    >
                      <FiEdit />
                    </ActionIconButton>
                    <ActionIconButton
                      className="delete"
                      onClick={() => handleDeleteMeal(meal.id)}
                      title="Delete"
                    >
                      <FiTrash2 />
                    </ActionIconButton>
                  </MealActions>
                </MealContent>
              </MealCard>
            ))}
          </MealsGrid>
        )}
      </MealManagementContent>
    </MealManagementContainer>
  );
};

export default MealManagement;

